import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback } from 'react';

import { ModalConversionPoints } from '@/components/Modals/Account';
import { signUpAccountTypeAtom } from '@/components/Modals/SignUp/atoms';
import { subscribeModalSelectedPlanAtom } from '@/components/Modals/Subscribe/atoms';
import {
  useNavigateToCheckout,
  useOpenPricingSignupDialog,
} from '@/components/DialogStage/pricingSignupFlow';
import { useHasRole, useIsLoggedIn, useSubscriptionType } from '@/hooks/auth';
import useBillingMode from '@/hooks/billing/useBillingMode';
import { upgradePlanModalPlanAtom } from '@/pages/pricing/atoms';
import { conversionPoints } from '@/components/Modals/context/ModalRoutingContext';
import { recordConversion } from '@/utils/analytics/conversion';

import { useSubscribeModal } from './useSubscribeModal';

import { useFeatureIsOn, useFeatureValue } from '@growthbook/growthbook-react';

import type { Plan } from '@/fixtures/plans';
import { useOverduePayment } from './useOverduePayment';
import { FLAG_PRICING_CARD_CTA } from '@/constants/growthbook-flags';
import { isBusinessPlanName } from '@/lib/pricing/businessPlans';

/**
 * Gates the active Downgrade CTA. The flow behind it is a separate project, so
 * until that ships lower tiers keep the greyed-out button they had before.
 */
const DOWNGRADES_FLAG = 'plan-downgrades';

/**
 * Possible button types for subscription-related actions
 * - manage: Current subscription plan, allows managing the subscription
 * - upgrade: Available higher tier plan that user can upgrade to
 * - start: Default action for new subscriptions
 * - disabled: Plans below current subscription tier
 * - free: Current plan when on free tier
 */
export type SubscriptionButtonType =
  | 'manage'
  | 'upgrade'
  | 'downgrade'
  | 'start'
  | 'disabled'
  | 'free';

/**
 * Props for the useSubscriptionButtonState hook
 */
export interface UseSubscriptionButtonStateProps {
  /** Array of subscription plans with their types */
  plans: Array<{ type: string }>;
}

/**
 * State object for each subscription button
 */
export interface ButtonState {
  /** Text to display on the button */
  buttonText: string;
  /** The type of button to display */
  buttonType: SubscriptionButtonType;
  /** Whether the button should be disabled */
  isDisabled: boolean;
  /** Click handler for the button */
  onClick: () => void;
}

/**
 * Map of button types to their display text
 */
const buttonTextMap = {
  manage: 'Manage plan',
  upgrade: 'Upgrade',
  downgrade: 'Downgrade',
  start: 'Get started',
  disabled: 'Get started',
  free: 'Your current plan',
} as const;

/**
 * Plan hierarchy map defining the upgrade path and relative positions
 * The number represents the tier level, higher number means higher tier
 */
const PLAN_HIERARCHY: Record<string, number> = {
  Free: 0,
  Essentials: 1,
  Creator: 2,
  Pro: 3,
  Business: 4,
} as const;

/**
 * Determines the relationship between two plans in the hierarchy
 */
function comparePlans(
  currentPlan: string,
  targetPlan: string
): 'current' | 'upgrade' | 'downgrade' {
  const currentTier = PLAN_HIERARCHY[currentPlan];
  const targetTier = PLAN_HIERARCHY[targetPlan];

  if (currentTier === targetTier) {
    return 'current';
  }

  if (targetTier > currentTier) {
    return 'upgrade';
  }

  return 'downgrade';
}

const getButtonText = (
  planType: string,
  subscribeCtaVariant: string,
  defaultText: string
) => {
  let buttonText: string = defaultText;
  /**
   * Special logic for the "Free" plan type button.
   * Use the current (default) text if part of the control group, otherwise use
   * "Start free" for the variant groups as it fits better.
   */
  if (planType.toLowerCase() === 'free') {
    if (subscribeCtaVariant === 'default') {
      return defaultText;
    }
    return 'Start free';
  }

  /**
   * Override the button text if the user is in an AB test variant group.
   */
  if (subscribeCtaVariant === 'subscribe-now') {
    buttonText = 'Subscribe now';
  } else if (subscribeCtaVariant === 'get-plan') {
    buttonText = `Get ${planType}`;
  }

  return buttonText;
};

/**
 * Hook to manage subscription button states across multiple subscription plans
 *
 * This hook handles the following scenarios:
 * 1. Logged-out users: All plans show 'Get started' buttons
 * 2. Free tier users: Current plan shows 'Your current plan', higher tiers show 'Subscribe'
 * 3. Paid tier users:
 *    - Current plan shows 'Manage'
 *    - Higher tiers show 'Upgrade' (disabled if overdue payment exists)
 *    - Lower tiers are disabled
 *
 * Scenarios 1 & 2 are currently subject to an AB test controlled by the `FLAG_PRICING_CARD_CTA` feature flag.
 *
 * @param props.plans - Array of subscription plans to generate button states for
 * @returns Array of button states corresponding to each plan
 *
 * @example
 * ```tsx
 * const { pricingPlans } = usePricingCards();
 * const buttonStates = useSubscriptionButtonState({ plans: pricingPlans });
 *
 * return plans.map((plan, index) => (
 *   <Button
 *     type={buttonStates[index].buttonType}
 *     disabled={buttonStates[index].isDisabled}
 *   >
 *     {buttonStates[index].buttonText}
 *   </Button>
 * ));
 * ```
 */
export function useSubscriptionButtonState({
  plans,
}: UseSubscriptionButtonStateProps): ButtonState[] {
  const subscriptionType = useSubscriptionType();
  const isLoggedIn = useIsLoggedIn();
  const router = useRouter();
  const openPricingSignupDialog = useOpenPricingSignupDialog();
  const navigateToCheckout = useNavigateToCheckout();
  const setSelectedPlan = useSetAtom(subscribeModalSelectedPlanAtom);
  const setSignUpAccountType = useSetAtom(signUpAccountTypeAtom);
  const setUpgradeModalPlan = useSetAtom(upgradePlanModalPlanAtom);

  const isFree = useHasRole('Free');
  const { openSubscribeModalWithConversion } = useSubscribeModal();
  const { overduePayment } = useOverduePayment();
  const { selectedBillingMode } = useBillingMode();
  const areDowngradesEnabled = useFeatureIsOn(DOWNGRADES_FLAG);
  const subscribeCtaVariant = useFeatureValue(FLAG_PRICING_CARD_CTA, 'default');

  const handleOpenModal = useCallback(
    (planType: string) => {
      // Set the account type atom for all plans so the correct plan is pre-selected
      setSignUpAccountType(planType as Plan['name']);

      if (!isLoggedIn) {
        // Open the central sign-up pop-up. Paid plans are stashed so a
        // successful signup routes to /subscribe checkout; Free has no
        // checkout step and falls through to the onboarding questionnaire.
        openPricingSignupDialog(planType, selectedBillingMode);
      } else if (isFree) {
        // If user is logged in and on free tier, show the subscribe modal
        setSelectedPlan(planType as Plan['name']);
        openSubscribeModalWithConversion(ModalConversionPoints.PricingPage);
      } else {
        // If user is logged in and on paid tier, show the upgrade modal
        setUpgradeModalPlan(planType as Plan['name']);
      }
    },
    [
      isFree,
      isLoggedIn,
      openPricingSignupDialog,
      selectedBillingMode,
      setSelectedPlan,
      setSignUpAccountType,
      setUpgradeModalPlan,
      openSubscribeModalWithConversion,
    ]
  );

  const createClickHandler = useCallback(
    (planType: string, buttonType: SubscriptionButtonType) => {
      return () => {
        switch (buttonType) {
          case 'manage':
            void router.push('/my/account');
            break;
          case 'upgrade':
          case 'downgrade':
            // Both are only ever assigned to paid subscribers, so we must
            // always route through the proration modal — never to
            // `/subscribe`, which is the new-subscription checkout and
            // will be rejected by the backend with a 422.
            handleOpenModal(planType);
            break;
          case 'start': {
            // `'start'` routes new paid subscriptions to `/subscribe`. Two
            // exceptions both open the central sign-up pop-up instead: Free,
            // because `/subscribe` is paid-only (DEV-6057), and anyone
            // logged out, because `/subscribe` is auth-gated (DEV-6729) —
            // sign-up pushes on to checkout itself once the account exists
            // (see usePricingSignupSuccessRedirect).
            const isFreePlan = planType.toLowerCase() === 'free';
            if (isFreePlan || !isLoggedIn) {
              handleOpenModal(planType);
            } else {
              // Logged-in Free-tier users upgrading to a paid plan are
              // already authenticated, so they skip straight to the
              // new-subscription checkout. Direct-to-checkout CTA bypasses
              // every modal, so record the pricing-page origin here or the
              // purchase lands with an empty conversion_source.
              recordConversion({
                source: conversionPoints[ModalConversionPoints.PricingPage],
              });
              navigateToCheckout(planType, selectedBillingMode);
            }
            break;
          }
          case 'disabled':
          case 'free':
            // These button types are disabled and don't need click handlers
            break;
          default:
            break;
        }
      };
    },
    [
      router,
      handleOpenModal,
      navigateToCheckout,
      selectedBillingMode,
      isLoggedIn,
    ]
  );

  // For logged-out users, all plans show 'Get started' buttons
  if (!isLoggedIn) {
    return plans.map((plan) => ({
      buttonType: 'start',
      buttonText: getButtonText(
        plan.type,
        subscribeCtaVariant,
        buttonTextMap.start
      ),
      isDisabled: false,
      onClick: createClickHandler(plan.type, 'start'),
    }));
  }

  return plans.map((plan) => {
    const planRelation = comparePlans(subscriptionType, plan.type);
    let buttonType: SubscriptionButtonType = 'start';

    switch (planRelation) {
      case 'current':
        buttonType = subscriptionType === 'Free' ? 'free' : 'manage';
        break;
      case 'upgrade':
        buttonType = subscriptionType === 'Free' ? 'start' : 'upgrade';
        // If there's an overdue payment, disable the upgrade button
        buttonType = overduePayment ? 'disabled' : buttonType;
        break;
      case 'downgrade':
        // Lower tiers get an active Downgrade CTA through the same in-app
        // plan-change modal (DEV-6405), once the downgrade flow exists.
        // Business can never downgrade (DEV-7425).
        buttonType =
          areDowngradesEnabled &&
          !overduePayment &&
          !isBusinessPlanName(subscriptionType)
            ? 'downgrade'
            : 'disabled';
        break;
      default:
        buttonType = 'disabled';
    }

    // Logged-in free users see 'Subscribe' rather than 'Get started' (DEV-6404).
    const buttonText: string =
      buttonType === 'start' && isFree
        ? getButtonText(plan.type, subscribeCtaVariant, 'Subscribe')
        : buttonTextMap[buttonType];

    return {
      buttonType,
      buttonText,
      isDisabled: buttonType === 'disabled' || buttonType === 'free',
      onClick: createClickHandler(plan.type, buttonType),
    };
  });
}
